/**
 * Frontend Logger
 * Browser-compatible logging with environment-aware levels
 * No external dependencies - uses native console methods
 */

class FrontendLogger {
  constructor() {
    // Determine if we're in development mode
    const isDev = window.location.hostname === 'localhost' ||
                   window.location.hostname.includes('dev') ||
                   window.location.hostname.includes('127.0.0.1') ||
                   window.location.protocol === 'file:';

    // Set log level based on environment
    this.level = isDev ? 'debug' : 'warn';

    // Define log levels with numeric values
    this.levels = {
      debug: 0,
      info: 1,
      warn: 2,
      error: 3
    };

    this.currentLevel = this.levels[this.level];
  }

  // Check if a log level should be output
  shouldLog(level) {
    return this.levels[level] >= this.currentLevel;
  }

  // Format log message with timestamp and metadata
  formatMessage(level, message, ...meta) {
    const timestamp = new Date().toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });

    // Color mapping for different log levels
    const colors = {
      debug: 'color: #666',
      info: 'color: #0066cc',
      warn: 'color: #ff9900',
      error: 'color: #cc0000'
    };

    // Format the message with timestamp and level
    const levelStr = level.toUpperCase().padEnd(5);
    let logLine = `${timestamp} [${levelStr}] ${message}`;

    // Add metadata if present
    if (meta.length > 0) {
      const metaStr = meta.map(item => {
        if (typeof item === 'object' && item !== null) {
          return Object.entries(item).map(([k, v]) => `${k}=${v}`).join(' ');
        }
        return String(item);
      }).join(' ');

      if (metaStr) {
        logLine += ` | ${metaStr}`;
      }
    }

    return { logLine, color: colors[level] || 'color: inherit' };
  }

  // Core logging method
  log(level, ...args) {
    if (!this.shouldLog(level)) return;

    const { logLine, color } = this.formatMessage(level, ...args);

    // Use appropriate console method with styling
    // eslint-disable-next-line no-console
    if (console[level]) {
      // eslint-disable-next-line no-console
      console[level](`%c${logLine}`, color);
    } else {
      // eslint-disable-next-line no-console
      console.log(`%c${logLine}`, color);
    }
  }

  // Helper methods for common logging patterns
  auth(action, userId = null, details = {}) {
    const detailsStr = Object.keys(details).length > 0 ?
      ` | ${Object.entries(details).map(([k,v]) => `${k}=${v}`).join(' ')}` : '';
    this.log('info', `Auth: ${action}${userId ? ` | userId=${userId}` : ''}${detailsStr}`);
  }

  api(method, endpoint, status, duration, userId) {
    const durationStr = duration ? ` | duration=${duration}ms` : '';
    const userIdStr = userId ? ` | userId=${userId}` : '';
    this.log('info', `API: ${method} ${endpoint} | status=${status}${durationStr}${userIdStr}`);
  }

  ui(action, component = null, details = {}) {
    const detailsStr = Object.keys(details).length > 0 ?
      ` | ${Object.entries(details).map(([k,v]) => `${k}=${v}`).join(' ')}` : '';
    this.log('info', `UI: ${action}${component ? ` | component=${component}` : ''}${detailsStr}`);
  }

  component(lifecycle, componentName, details = {}) {
    const detailsStr = Object.keys(details).length > 0 ?
      ` | ${Object.entries(details).map(([k,v]) => `${k}=${v}`).join(' ')}` : '';
    this.log('debug', `Component: ${lifecycle} | name=${componentName}${detailsStr}`);
  }

  router(action, path = null, details = {}) {
    const detailsStr = Object.keys(details).length > 0 ?
      ` | ${Object.entries(details).map(([k,v]) => `${k}=${v}`).join(' ')}` : '';
    this.log('info', `Router: ${action}${path ? ` | path=${path}` : ''}${detailsStr}`);
  }

  // Standard logging methods
  debug(...args) { this.log('debug', ...args); }
  info(...args) { this.log('info', ...args); }
  warn(...args) { this.log('warn', ...args); }
  error(...args) { this.log('error', ...args); }

  // Additional utility methods
  setLevel(level) {
    // eslint-disable-next-line no-prototype-builtins
    if (this.levels.hasOwnProperty(level)) {
      this.level = level;
      this.currentLevel = this.levels[level];
    }
  }

  getLevel() { return this.level; }

  // Enable all logging (useful for debugging)
  enableAll() { this.setLevel('debug'); }

  // Disable all logging except errors
  disableAll() { this.setLevel('error'); }
}

export default new FrontendLogger();
